import { useEffect, useMemo, useState } from 'react';
import { Calendar, User, Clock, DollarSign } from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, EmptyState, Select } from '../components/ui';

/**
 * Combined "what's booked at my club" view.
 *  - Court bookings (court_bookings WHERE club_id = clubId)
 *  - Coach lessons hosted at this club (coach_bookings WHERE club_id = clubId)
 * Rendered as a unified chronological list with filters for range + kind.
 */

type Range = 'today' | 'week' | 'month' | 'all_upcoming';

type CourtRow = {
  id: string;
  kind: 'court';
  when: string;             // ISO start
  until: string;            // ISO end
  ref: string;
  total: number;
  currency: string;
  status: string;
  court_name: string | null;
  customer: string | null;
};

type CoachRow = {
  id: string;
  kind: 'coach';
  when: string;
  until: string;
  ref: string;
  total: number;
  currency: string;
  status: string;
  coach_name: string | null;
  lesson: string | null;
  customer: string | null;
};

type Row = CourtRow | CoachRow;

export default function ClubBookings({ clubId }: { clubId: string }) {
  const [range, setRange] = useState<Range>('week');
  const [kind, setKind] = useState<'all' | 'court' | 'coach'>('all');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const [startISO, endISO] = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    let end = new Date(now);
    if (range === 'today') {
      end = new Date(start.getTime() + 86400_000);
    } else if (range === 'week') {
      end = new Date(start.getTime() + 7 * 86400_000);
    } else if (range === 'month') {
      end = new Date(start.getTime() + 30 * 86400_000);
    } else {
      end = new Date(start.getTime() + 365 * 86400_000);
    }
    return [start.toISOString(), end.toISOString()];
  }, [range]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [courtRes, coachRes] = await Promise.all([
        kind === 'coach'
          ? Promise.resolve({ data: [] })
          : supabase
              .from('court_bookings')
              .select(`
                id, booking_reference, start_at, end_at, status, total_amount, currency,
                courts(id, name),
                profiles!user_id(full_name, user_id)
              `)
              .eq('club_id', clubId)
              .gte('start_at', startISO)
              .lt('start_at', endISO)
              .in('status', ['confirmed', 'pending_payment', 'completed'])
              .order('start_at'),
        kind === 'court'
          ? Promise.resolve({ data: [] })
          : supabase
              .from('coach_bookings')
              .select(`
                id, booking_reference, scheduled_at, ends_at, status, total_amount, currency,
                lesson_type, environment,
                coaches(id, display_name),
                profiles!user_id(full_name, user_id)
              `)
              .eq('club_id', clubId)
              .gte('scheduled_at', startISO)
              .lt('scheduled_at', endISO)
              .in('status', ['confirmed', 'pending_payment', 'completed'])
              .order('scheduled_at'),
      ]);

      const courtRows: CourtRow[] = ((courtRes.data ?? []) as any[]).map((b) => ({
        id: b.id,
        kind: 'court',
        when: b.start_at,
        until: b.end_at,
        ref: b.booking_reference,
        total: Number(b.total_amount ?? 0),
        currency: b.currency,
        status: b.status,
        court_name: b.courts?.name ?? null,
        customer: b.profiles?.full_name ?? null,
      }));
      const coachRows: CoachRow[] = ((coachRes.data ?? []) as any[]).map((b) => ({
        id: b.id,
        kind: 'coach',
        when: b.scheduled_at,
        until: b.ends_at,
        ref: b.booking_reference,
        total: Number(b.total_amount ?? 0),
        currency: b.currency,
        status: b.status,
        coach_name: b.coaches?.display_name ?? null,
        lesson: `${b.lesson_type.replace('_', ' ')} · ${b.environment}`,
        customer: b.profiles?.full_name ?? null,
      }));
      const merged: Row[] = [...courtRows, ...coachRows].sort(
        (a, b) => new Date(a.when).getTime() - new Date(b.when).getTime(),
      );
      setRows(merged);
      setLoading(false);
    })();
  }, [clubId, startISO, endISO, kind]);

  const totalRevenue = rows
    .filter((r) => ['confirmed', 'completed'].includes(r.status))
    .reduce((s, r) => s + r.total, 0);

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="Club"
        title="Bookings"
        subtitle="Everything on the courts and with your coaches, in one chronological list."
        actions={
          <div className="flex gap-2">
            <Select value={kind} onChange={(e) => setKind(e.target.value as any)} className="w-40">
              <option value="all">All</option>
              <option value="court">Courts only</option>
              <option value="coach">Lessons only</option>
            </Select>
            <Select value={range} onChange={(e) => setRange(e.target.value as Range)} className="w-40">
              <option value="today">Today</option>
              <option value="week">Next 7 days</option>
              <option value="month">Next 30 days</option>
              <option value="all_upcoming">Next 365 days</option>
            </Select>
          </div>
        }
      />

      <Section
        title={`${rows.length} booking${rows.length === 1 ? '' : 's'}`}
        subtitle={`Confirmed revenue: ${rows[0]?.currency ?? 'AED'} ${totalRevenue.toFixed(0)}`}
        className="p-0 overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="Nothing booked in this range"
            description="Customers' future bookings will show up here as they come in."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">When</th>
                <th className="text-left px-5 py-3 font-semibold">Kind</th>
                <th className="text-left px-5 py-3 font-semibold">Details</th>
                <th className="text-left px-5 py-3 font-semibold">Customer</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.kind}-${r.id}`} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3 whitespace-nowrap">
                    <div className="text-gray-900">{fmtDate(r.when)}</div>
                    <div className="text-xs text-gray-500">{fmtTime(r.when)} – {fmtTime(r.until)}</div>
                  </td>
                  <td className="px-5 py-3">
                    {r.kind === 'court'
                      ? <Badge tone="blue" icon={Clock}>Court</Badge>
                      : <Badge tone="lime" icon={User}>Lesson</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    {r.kind === 'court'
                      ? <div>{r.court_name ?? '—'}</div>
                      : (
                        <div>
                          <div>{r.coach_name ?? '—'}</div>
                          <div className="text-xs text-gray-500 capitalize">{r.lesson}</div>
                        </div>
                      )}
                  </td>
                  <td className="px-5 py-3 text-gray-700">{r.customer ?? '—'}</td>
                  <td className="px-5 py-3">
                    <Badge tone={
                      r.status === 'confirmed' ? 'green'
                      : r.status === 'pending_payment' ? 'amber'
                      : r.status === 'completed' ? 'gray'
                      : 'red'
                    }>{r.status.replace('_', ' ')}</Badge>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="text-gray-900 flex items-center justify-end gap-1">
                      <DollarSign className="w-3 h-3 text-gray-400" />
                      {r.currency} {r.total.toFixed(0)}
                    </div>
                    <div className="text-[10px] text-gray-400">{r.ref}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
